/**
 * Evaluator — turns a Timeline into geometry, incrementally.
 *
 * Regeneration model (Fusion 360-style):
 *  1. Walk active features in timeline order (already topological).
 *  2. Compute each node's content hash = H(type, params, suppressed,
 *     dependency result hashes).
 *  3. Cache hit → reuse the previous GeometryResult without touching the
 *     kernel. Editing feature k re-executes only the downstream cone of k.
 *  4. Kernel failure marks the node 'error'; dependents become 'blocked'
 *     but *independent* later features still evaluate (partial regen).
 *
 * The evaluator is transport-agnostic: pass a kernel that runs inline (tests)
 * or one that proxies to a Web Worker via Comlink (production).
 */

import { containsExprs, evalParamTable, resolveExprs } from '../expr';
import { signedVolume } from '../geometry/meshUtils';
import { resolveMaterialChain } from '../materials';
import { solveSketch } from '../geometry/constraints';
import type { EvalContext, GeomKernel, GeometryResult, MeshData } from '../geometry/types';
import { hashValue } from './hash';
import { Timeline, type TimelineJSON } from './timeline';
import {
  ref,
  type FeatureId,
  type FeatureNode,
  type FeatureParams,
  type MateAnchor,
  type MateParams,
  type PartParams,
  type SketchParams,
} from './types';

/** A part's source project: its recipe + a content hash for cache identity. */
export interface PartSource {
  timeline: TimelineJSON;
  contentHash: string;
}

/** Loads an inserted project by id (IndexedDB in the app; stubs in tests). */
export type PartResolver = (projectId: string) => Promise<PartSource | null>;

/** Assembly nesting bound — a backstop, not a real design size. */
const MAX_ASSEMBLY_DEPTH = 8;

/** Bbox anchor point of a body result. */
function anchorPoint(mesh: MeshData, anchor: MateAnchor): [number, number, number] {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  const p = mesh.positions;
  for (let i = 0; i < p.length; i += 3) {
    for (let k = 0; k < 3; k++) {
      min[k] = Math.min(min[k]!, p[i + k]!);
      max[k] = Math.max(max[k]!, p[i + k]!);
    }
  }
  const cx = (min[0]! + max[0]!) / 2;
  const cy = (min[1]! + max[1]!) / 2;
  switch (anchor) {
    case 'center': return [cx, cy, (min[2]! + max[2]!) / 2];
    case 'bottom': return [cx, cy, min[2]!];
    case 'top': return [cx, cy, max[2]!];
    case 'min': return [min[0]!, min[1]!, min[2]!];
    case 'max': return [max[0]!, max[1]!, max[2]!];
  }
}

/** Lower a pattern to the equivalent transform-array feature node. */
function lowerPattern(feature: FeatureNode): FeatureNode {
  const p = feature.params as import('./types').PatternParams;
  const count = Math.round(p.count as number);
  if (!Number.isFinite(count) || count < 2) {
    throw new Error('Pattern count must be at least 2');
  }
  if (count > 201) throw new Error('Pattern count is capped at 201');
  let translation: [number, number, number] = [0, 0, 0];
  let rotationAngle = 0;
  if (p.kind === 'linear') {
    const s = p.step ?? [20, 0, 0];
    translation = [s[0], s[1], s[2]];
    if (Math.hypot(...translation) < 1e-9) throw new Error('Linear pattern step is zero');
  } else {
    const total = ((p.totalAngleDeg as number | undefined) ?? 360) * (Math.PI / 180);
    // Full circle: N evenly spaced WITHOUT overlap at 360°; partial spans
    // place first..last inclusive.
    const fullCircle = Math.abs(Math.abs(total) - Math.PI * 2) < 1e-9;
    rotationAngle = fullCircle ? total / count : total / (count - 1);
  }
  return {
    ...feature,
    type: 'transform',
    params: {
      target: p.target,
      translation,
      rotationAxis: p.axis ?? 'Z',
      rotationAngle,
      rotationCenter: p.center ?? [0, 0, 0],
      copies: count - 1,
    },
  } as FeatureNode;
}

/** Nearest cylindrical face of a body to a pick-time centroid → its axis. */
function cylinderAxis(
  mesh: MeshData,
  near: readonly [number, number, number] | readonly number[],
): { point: [number, number, number]; dir: [number, number, number] } {
  const kinds = mesh.faceKinds;
  const d = mesh.faceData;
  if (!kinds || !d) throw new Error('Concentric mates need exact B-rep bodies');
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < kinds.length; i++) {
    if (kinds[i] !== 2) continue;
    const dist = Math.hypot(d[i * 10]! - near[0]!, d[i * 10 + 1]! - near[1]!, d[i * 10 + 2]! - near[2]!);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  if (best < 0) throw new Error('Body has no cylindrical face for this mate');
  const dir: [number, number, number] = [d[best * 10 + 3]!, d[best * 10 + 4]!, d[best * 10 + 5]!];
  const len = Math.hypot(...dir) || 1;
  return {
    point: [d[best * 10 + 6]!, d[best * 10 + 7]!, d[best * 10 + 8]!],
    dir: [dir[0] / len, dir[1] / len, dir[2] / len],
  };
}

/**
 * Rigid motion (rotation R about the world origin, then translation t) that
 * realizes a planar face mate: bring target face A (point pA, unit normal nA)
 * to face reference face B (pB, nB). Pure + exported so the sign-sensitive
 * angle/flush math is unit-testable without the kernel.
 *  - flush/distance: nA → −nB (faces abut), slide along nB to gap (0 = touch),
 *    in-plane position free.
 *  - parallel: nA → nB, pin pA in place (pure reorientation).
 *  - angle: nA held `angleDeg` from nB, pin pA in place.
 */
export function planarMateRT(
  pA: readonly number[], nA: readonly number[],
  pB: readonly number[], nB: readonly number[],
  kind: 'flush' | 'parallel' | 'distance' | 'angle',
  gap = 0,
  angleDeg = 0,
): { R: number[]; t: [number, number, number] } {
  const dot = nA[0]! * nB[0]! + nA[1]! * nB[1]! + nA[2]! * nB[2]!;
  let R: number[];
  if (kind === 'parallel') {
    R = rotationBetween([...nA], [...nB]);
  } else if (kind === 'angle') {
    const theta = angleDeg * (Math.PI / 180);
    const phi = Math.acos(Math.max(-1, Math.min(1, dot)));
    let axis = [
      nA[1]! * nB[2]! - nA[2]! * nB[1]!,
      nA[2]! * nB[0]! - nA[0]! * nB[2]!,
      nA[0]! * nB[1]! - nA[1]! * nB[0]!,
    ];
    if (Math.hypot(...axis) < 1e-9) {
      const p = Math.abs(nA[0]!) < 0.9 ? [1, 0, 0] : [0, 1, 0];
      axis = [
        nA[1]! * p[2]! - nA[2]! * p[1]!,
        nA[2]! * p[0]! - nA[0]! * p[2]!,
        nA[0]! * p[1]! - nA[1]! * p[0]!,
      ];
    }
    // +rotation about (nA×nB) moves nA TOWARD nB (by +phi it lands on nB), so
    // leaving residual angle theta needs (phi − theta), not (theta − phi).
    R = axisAngle(axis, phi - theta);
  } else {
    R = rotationBetween([...nA], nB.map((v) => -v));
  }
  const rp: [number, number, number] = [
    R[0]! * pA[0]! + R[1]! * pA[1]! + R[2]! * pA[2]!,
    R[3]! * pA[0]! + R[4]! * pA[1]! + R[5]! * pA[2]!,
    R[6]! * pA[0]! + R[7]! * pA[1]! + R[8]! * pA[2]!,
  ];
  let t: [number, number, number];
  if (kind === 'flush' || kind === 'distance') {
    // Slide only along B's normal so the planes sit `gap` apart (0 = touch);
    // in-plane position is left free (matches concentric's axial freedom).
    const sd = (rp[0] - pB[0]!) * nB[0]! + (rp[1] - pB[1]!) * nB[1]! + (rp[2] - pB[2]!) * nB[2]!;
    const move = gap - sd;
    t = [nB[0]! * move, nB[1]! * move, nB[2]! * move];
  } else {
    // parallel / angle: pin pA in place (the part op rotates about the world
    // origin), so a body far from the origin reorients rather than swinging.
    t = [pA[0]! - rp[0], pA[1]! - rp[1], pA[2]! - rp[2]];
  }
  return { R, t };
}

/** Nearest planar face of a body to a pick-time centroid → point + outward normal. */
function planarFace(
  mesh: MeshData,
  near: readonly [number, number, number] | readonly number[],
): { point: [number, number, number]; normal: [number, number, number] } {
  const kinds = mesh.faceKinds;
  const d = mesh.faceData;
  if (!kinds || !d) throw new Error('Planar mates need exact B-rep bodies');
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < kinds.length; i++) {
    if (kinds[i] !== 1) continue;
    const dist = Math.hypot(d[i * 10]! - near[0]!, d[i * 10 + 1]! - near[1]!, d[i * 10 + 2]! - near[2]!);
    if (dist < bestDist) { bestDist = dist; best = i; }
  }
  if (best < 0) throw new Error('Body has no planar face near the mate point');
  const n: [number, number, number] = [d[best * 10 + 3]!, d[best * 10 + 4]!, d[best * 10 + 5]!];
  const len = Math.hypot(...n) || 1;
  return {
    point: [d[best * 10]!, d[best * 10 + 1]!, d[best * 10 + 2]!],
    normal: [n[0] / len, n[1] / len, n[2] / len],
  };
}

/** Rotation matrix (row-major) about a unit axis by `angle` (Rodrigues). */
function axisAngle(axis: number[], angle: number): number[] {
  const l = Math.hypot(axis[0]!, axis[1]!, axis[2]!) || 1;
  const [x, y, z] = [axis[0]! / l, axis[1]! / l, axis[2]! / l];
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  return [
    t * x * x + c, t * x * y - s * z, t * x * z + s * y,
    t * x * y + s * z, t * y * y + c, t * y * z - s * x,
    t * x * z - s * y, t * y * z + s * x, t * z * z + c,
  ];
}

/** Rotation matrix (row-major 3×3) taking unit vector a onto unit vector b. */
function rotationBetween(a: number[], b: number[]): number[] {
  const c = a[0]! * b[0]! + a[1]! * b[1]! + a[2]! * b[2]!;
  const k = [
    a[1]! * b[2]! - a[2]! * b[1]!,
    a[2]! * b[0]! - a[0]! * b[2]!,
    a[0]! * b[1]! - a[1]! * b[0]!,
  ];
  const s2 = k[0]! * k[0]! + k[1]! * k[1]! + k[2]! * k[2]!;
  if (s2 < 1e-18) {
    if (c > 0) return [1, 0, 0, 0, 1, 0, 0, 0, 1];
    // Antiparallel: 180° about any axis ⊥ a.
    const p = Math.abs(a[0]!) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    const ax = [
      a[1]! * p[2]! - a[2]! * p[1]!,
      a[2]! * p[0]! - a[0]! * p[2]!,
      a[0]! * p[1]! - a[1]! * p[0]!,
    ];
    const l = Math.hypot(...ax) || 1;
    const [x, y, z] = [ax[0]! / l, ax[1]! / l, ax[2]! / l];
    return [
      2 * x * x - 1, 2 * x * y, 2 * x * z,
      2 * x * y, 2 * y * y - 1, 2 * y * z,
      2 * x * z, 2 * y * z, 2 * z * z - 1,
    ];
  }
  // Rodrigues: R = I + K + K²·(1−c)/s².
  const f = (1 - c) / s2;
  const [kx, ky, kz] = [k[0]!, k[1]!, k[2]!];
  return [
    1 + f * (-kz * kz - ky * ky), -kz + f * kx * ky, ky + f * kx * kz,
    kz + f * kx * ky, 1 + f * (-kz * kz - kx * kx), -kx + f * ky * kz,
    -ky + f * kx * kz, kx + f * ky * kz, 1 + f * (-ky * ky - kx * kx),
  ];
}

/** Euler XYZ (for R = Rz·Ry·Rx — the part op's convention) from a matrix. */
function eulerZYX(R: number[]): [number, number, number] {
  const r20 = Math.max(-1, Math.min(1, R[6]!));
  const ry = Math.asin(-r20);
  if (Math.abs(r20) > 0.999999) {
    // Gimbal: fold everything into rx.
    return [Math.atan2(-R[1]!, R[4]!), ry, 0];
  }
  return [Math.atan2(R[7]!, R[8]!), ry, Math.atan2(R[3]!, R[0]!)];
}

/** Lower a mate to the equivalent rigid-motion feature node. */
function lowerMate(feature: FeatureNode, ctx: EvalContext): FeatureNode {
  const params = feature.params as MateParams;
  const target = ctx.inputs.get(params.target.$feature);
  const to = ctx.inputs.get(params.to.$feature);
  if (target?.kind !== 'body' || to?.kind !== 'body') {
    throw new Error('Mate requires two bodies');
  }
  if (target.mesh.positions.length === 0 || to.mesh.positions.length === 0) {
    throw new Error('Mate bodies have no geometry');
  }

  if (params.kind === 'concentric') {
    if (!params.faceTarget || !params.faceTo) {
      throw new Error('Concentric mate is missing its picked faces');
    }
    const axA = cylinderAxis(target.mesh, params.faceTarget);
    const axB = cylinderAxis(to.mesh, params.faceTo);
    // Direction sign is arbitrary — take the smaller rotation.
    const dot =
      axA.dir[0] * axB.dir[0] + axA.dir[1] * axB.dir[1] + axA.dir[2] * axB.dir[2];
    const bDir = dot < 0 ? axB.dir.map((v) => -v) : [...axB.dir];
    const R = rotationBetween([...axA.dir], bDir);
    // Rotate about the origin, then translate so the axes COINCIDE while
    // preserving the part's position ALONG the shared axis.
    const rp: [number, number, number] = [
      R[0]! * axA.point[0] + R[1]! * axA.point[1] + R[2]! * axA.point[2],
      R[3]! * axA.point[0] + R[4]! * axA.point[1] + R[5]! * axA.point[2],
      R[6]! * axA.point[0] + R[7]! * axA.point[1] + R[8]! * axA.point[2],
    ];
    const delta = [axB.point[0] - rp[0], axB.point[1] - rp[1], axB.point[2] - rp[2]];
    const axial = delta[0]! * bDir[0]! + delta[1]! * bDir[1]! + delta[2]! * bDir[2]!;
    const t = [
      delta[0]! - axial * bDir[0]!,
      delta[1]! - axial * bDir[1]!,
      delta[2]! - axial * bDir[2]!,
    ];
    return {
      ...feature,
      type: 'part', // the part op = rotate about origin (Euler), then move
      params: {
        targets: [params.target],
        position: t,
        rotationEuler: eulerZYX(R),
      },
    } as FeatureNode;
  }

  if (
    params.kind === 'flush' || params.kind === 'parallel' ||
    params.kind === 'distance' || params.kind === 'angle'
  ) {
    if (!params.faceTarget || !params.faceTo) {
      throw new Error(`${params.kind} mate is missing its picked planar faces`);
    }
    const fA = planarFace(target.mesh, params.faceTarget);
    const fB = planarFace(to.mesh, params.faceTo);
    const { R, t } = planarMateRT(
      fA.point, fA.normal, fB.point, fB.normal, params.kind,
      params.kind === 'distance' ? ((params.gap as number) ?? 0) : 0,
      (params.angleDeg as number) ?? 0,
    );
    return {
      ...feature,
      type: 'part', // rotate about origin (Euler), then translate
      params: { targets: [params.target], position: t, rotationEuler: eulerZYX(R) },
    } as FeatureNode;
  }

  const a = anchorPoint(target.mesh, params.anchorTarget);
  const b = anchorPoint(to.mesh, params.anchorTo);
  return {
    ...feature,
    type: 'transform',
    params: {
      target: params.target,
      translation: [
        b[0] - a[0] + params.offset[0],
        b[1] - a[1] + params.offset[1],
        b[2] - a[2] + params.offset[2],
      ],
      rotationAxis: 'Z',
      rotationAngle: 0,
      rotationCenter: [0, 0, 0],
      copies: 0,
    },
  } as FeatureNode;
}

export type FeatureStatus =
  | { readonly state: 'ok'; readonly fromCache: boolean }
  | { readonly state: 'suppressed' }
  | { readonly state: 'rolled-back' }
  | { readonly state: 'error'; readonly message: string }
  | { readonly state: 'blocked'; readonly blockedBy: FeatureId };

export interface EvaluationReport {
  readonly statuses: ReadonlyMap<FeatureId, FeatureStatus>;
  readonly results: ReadonlyMap<FeatureId, GeometryResult>;
  /** Body results absorbed by later boolean features — hidden from display. */
  readonly consumed: ReadonlySet<FeatureId>;
  /** Count of kernel executions this pass (0 on a pure cache replay). */
  readonly executed: number;
  /**
   * The evaluator generation this report belongs to. A concurrent evaluate()
   * bumps the generation and abandons older passes, which then return an
   * incomplete report — callers compare this to `evaluator.currentGeneration`
   * and DISCARD any report that is no longer the newest.
   */
  readonly generation: number;
}

interface CacheEntry {
  readonly hash: string;
  readonly result: GeometryResult;
}

export class TimelineEvaluator {
  /** featureId → last successful evaluation; hash decides reuse. */
  private cache = new Map<FeatureId, CacheEntry>();
  private generation = 0;

  /** Newest generation dispatched — a report with a lower one is stale. */
  get currentGeneration(): number {
    return this.generation;
  }

  constructor(
    private readonly kernel: GeomKernel,
    private readonly resolvePart?: PartResolver,
    /** Project ids on the current assembly path (cycle guard); root first. */
    private readonly projectStack: readonly string[] = [],
  ) {}

  invalidateAll(): void {
    this.cache.clear();
  }

  /**
   * Evaluate the timeline. Concurrent-safe: if evaluate() is called again
   * while a pass is awaiting the kernel, the stale pass abandons its work
   * (its report is still returned but marked by a newer generation existing).
   */
  async evaluate(timeline: Timeline): Promise<EvaluationReport> {
    const gen = ++this.generation;
    // Parametric expressions: evaluate the document parameter table once per
    // pass; features referencing a broken parameter error individually below.
    const paramTable = evalParamTable(timeline.params);
    const statuses = new Map<FeatureId, FeatureStatus>();
    const results = new Map<FeatureId, GeometryResult>();
    const hashes = new Map<FeatureId, string>();
    /** Root cause blocking each failed subtree. */
    const failedRoot = new Map<FeatureId, FeatureId>();
    /** Bodies visible so far: implicit targets for boolean features. */
    const visible = new Map<FeatureId, { hash: string; mesh: MeshData; brepId?: string }>();
    const consumed = new Set<FeatureId>();
    let executed = 0;

    for (let i = 0; i < timeline.features.length; i++) {
      const feature = timeline.features[i]!;

      if (i >= timeline.rollbackIndex) {
        statuses.set(feature.id, { state: 'rolled-back' });
        continue;
      }
      if (feature.suppressed) {
        statuses.set(feature.id, { state: 'suppressed' });
        // Participates in downstream hashes so unsuppressing invalidates them.
        hashes.set(feature.id, hashValue({ suppressed: feature.id }));
        continue;
      }

      // A dependency that errored/was suppressed-away blocks this feature.
      const badDep = feature.dependencies.find(
        (d) => failedRoot.has(d) || !results.has(d),
      );
      if (badDep !== undefined) {
        const root = failedRoot.get(badDep) ?? badDep;
        failedRoot.set(feature.id, root);
        statuses.set(feature.id, { state: 'blocked', blockedBy: root });
        continue;
      }

      // Resolve {$expr} markers → numbers, then solve sketch constraints.
      // The RESOLVED params are this feature's identity: they feed both the
      // content hash (param edits regenerate dependents) and the kernel.
      let params: FeatureParams = feature.params;
      try {
        if (containsExprs(params)) {
          params = resolveExprs(params, paramTable.values) as FeatureParams;
        }
        if (feature.type === 'sketch') {
          const sp = params as SketchParams;
          if (sp.constraints && sp.constraints.length > 0) {
            const solved = solveSketch(sp.entities, sp.constraints);
            if (!solved.converged) {
              throw new Error(
                `Constraints unsatisfiable (residual ${solved.maxResidual.toExponential(1)})` +
                  ' — check for contradictory dimensions',
              );
            }
            params = { ...sp, entities: solved.entities };
          }
        }
      } catch (err) {
        failedRoot.set(feature.id, feature.id);
        statuses.set(feature.id, {
          state: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
        continue;
      }

      // Assembly components: evaluate the SOURCE PROJECT's timeline
      // recursively, then compound + place its bodies under this feature.
      if (feature.type === 'part') {
        const outcome = await this.evaluatePart(feature, params as PartParams, gen);
        if (gen !== this.generation) return { statuses, results, consumed, executed, generation: gen };
        if (outcome.kind === 'error') {
          failedRoot.set(feature.id, feature.id);
          statuses.set(feature.id, { state: 'error', message: outcome.message });
        } else {
          hashes.set(feature.id, outcome.hash);
          results.set(feature.id, outcome.result);
          statuses.set(feature.id, { state: 'ok', fromCache: outcome.fromCache });
          if (!outcome.fromCache) executed++;
          if (outcome.result.kind === 'body') {
            visible.set(feature.id, {
              hash: outcome.hash,
              mesh: outcome.result.mesh,
              brepId: outcome.result.brepId,
            });
          }
        }
        continue;
      }

      // Boolean features implicitly consume the whole visible body set, so
      // their identity must fold in those bodies' hashes — editing the plate
      // re-executes the hole cut through it.
      const op = (params as { op?: string }).op;
      const usesWorld = op !== undefined && op !== 'new-body';
      const contentHash = hashValue({
        type: feature.type,
        params,
        deps: feature.dependencies.map((d) => hashes.get(d)),
        world: usesWorld ? [...visible.values()].map((v) => v.hash) : undefined,
      });
      hashes.set(feature.id, contentHash);

      const recordBody = (result: GeometryResult): void => {
        if (result.kind !== 'body') return;
        for (const c of result.consumed ?? []) {
          visible.delete(c);
          consumed.add(c);
        }
        visible.set(feature.id, {
          hash: contentHash,
          mesh: result.mesh,
          brepId: result.brepId,
        });
      };

      const cached = this.cache.get(feature.id);
      if (cached && cached.hash === contentHash) {
        results.set(feature.id, cached.result);
        statuses.set(feature.id, { state: 'ok', fromCache: true });
        recordBody(cached.result);
        continue;
      }

      const ctx: EvalContext = {
        inputs: new Map(
          feature.dependencies.map((d) => [d, results.get(d)!] as const),
        ),
        worldBodies: new Map(
          [...visible].map(([id, v]) => [id, { mesh: v.mesh, brepId: v.brepId }] as const),
        ),
      };
      try {
        // Kernels receive resolved params (plain numbers, solved sketches).
        let effective: FeatureNode =
          params === feature.params ? feature : { ...feature, params };
        // Mates lower to a plain transform: compute the anchor-to-anchor
        // translation from the input bodies, then let the kernel move the
        // target exactly like a Move feature (consumes it).
        if (feature.type === 'mate') effective = lowerMate(effective, ctx);
        // Patterns lower to transform arrays (original = instance #0).
        if (feature.type === 'pattern') effective = lowerPattern(effective);
        // Superseded passes must not DISPATCH kernel work — the single-
        // threaded worker would grind through every stale drag frame.
        if (gen !== this.generation) return { statuses, results, consumed, executed, generation: gen };
        let result = await this.kernel.execute(effective, ctx);
        // Concentric mates lower to the part op, which doesn't consume —
        // a mate ALWAYS consumes the body it moves.
        if (feature.type === 'mate' && result.kind === 'body' && !result.consumed) {
          result = {
            ...result,
            consumed: [(feature.params as MateParams).target.$feature],
          };
        }
        if (gen !== this.generation) return { statuses, results, consumed, executed, generation: gen }; // superseded
        executed++;
        this.cache.set(feature.id, { hash: contentHash, result });
        results.set(feature.id, result);
        statuses.set(feature.id, { state: 'ok', fromCache: false });
        recordBody(result);
      } catch (err) {
        if (gen !== this.generation) return { statuses, results, consumed, executed, generation: gen };
        this.cache.delete(feature.id);
        failedRoot.set(feature.id, feature.id);
        statuses.set(feature.id, {
          state: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }

    // Drop cache entries for features no longer in the document.
    const live = new Set(timeline.features.map((f) => f.id));
    for (const id of this.cache.keys()) {
      if (!live.has(id)) this.cache.delete(id);
    }

    return { statuses, results, consumed, executed, generation: gen };
  }

  /**
   * One assembly component: resolve + evaluate the source project with a
   * FRESH sub-evaluator (cycle-guarded stack), then compound + place its
   * visible bodies under this feature via the kernel's 'part' op. The
   * source's content hash feeds this feature's identity, so editing the
   * part project regenerates every assembly that uses it. Sub-feature
   * kernel shapes are transient — the post-eval prune clears them; the
   * compound registered under THIS feature id is what persists.
   */
  private async evaluatePart(
    feature: FeatureNode,
    params: PartParams,
    gen: number,
  ): Promise<
    | { kind: 'error'; message: string }
    | { kind: 'ok'; hash: string; result: GeometryResult; fromCache: boolean }
  > {
    try {
      if (!this.resolvePart) throw new Error('Assemblies are unavailable in this context');
      if (this.projectStack.includes(params.projectId)) {
        throw new Error(`Circular assembly — "${params.source}" ultimately contains this project`);
      }
      if (this.projectStack.length >= MAX_ASSEMBLY_DEPTH) {
        throw new Error(`Assembly nesting deeper than ${MAX_ASSEMBLY_DEPTH} levels`);
      }
      const src = await this.resolvePart(params.projectId);
      if (!src) throw new Error(`Source project "${params.source}" no longer exists`);

      const hash = hashValue({ type: 'part', params, src: src.contentHash });
      const cached = this.cache.get(feature.id);
      if (cached && cached.hash === hash) {
        return { kind: 'ok', hash, result: cached.result, fromCache: true };
      }

      const subEval = new TimelineEvaluator(this.kernel, this.resolvePart, [
        ...this.projectStack,
        params.projectId,
      ]);
      const subTimeline = Timeline.fromJSON(src.timeline);
      const subReport = await subEval.evaluate(subTimeline);
      if (gen !== this.generation) return { kind: 'error', message: 'superseded' };

      const bodies: [FeatureId, GeometryResult][] = [];
      for (const [id, r] of subReport.results) {
        if (r.kind === 'body' && !subReport.consumed.has(id) && r.mesh.positions.length > 0) {
          bodies.push([id, r]);
        }
      }
      // Resolve each component's material IN ITS SOURCE PROJECT before the
      // sub-report is discarded — assemblies render/weigh components with it.
      const componentMaterials = bodies.map(([id, r]) => {
        const resolved = resolveMaterialChain(
          (fid) => subTimeline.get(fid as FeatureId),
          (fid) => subReport.results.get(fid as FeatureId),
          id,
        );
        return {
          ...(resolved?.material ? { material: resolved.material.id } : {}),
          ...(resolved?.color ? { color: resolved.color } : {}),
          volume: r.kind === 'body' ? Math.abs(signedVolume(r.mesh.positions, r.mesh.indices)) : 0,
        };
      });
      if (bodies.length === 0) {
        const err = [...subReport.statuses.values()].find((s) => s.state === 'error');
        throw new Error(
          `"${params.source}" has no bodies to insert` +
            (err && err.state === 'error' ? ` — it has errors: ${err.message}` : ''),
        );
      }

      const synthetic: FeatureNode = {
        id: feature.id,
        type: 'part',
        name: feature.name,
        // Kernels see the component bodies as ordinary target refs.
        params: { ...params, targets: bodies.map(([id]) => ref(id)) } as never,
        dependencies: [],
        suppressed: false,
      };
      const ctx: EvalContext = { inputs: new Map(bodies), worldBodies: new Map() };
      const executed = await this.kernel.execute(synthetic, ctx);
      const result: GeometryResult =
        executed.kind === 'body' ? { ...executed, componentMaterials } : executed;
      this.cache.set(feature.id, { hash, result });
      return { kind: 'ok', hash, result, fromCache: false };
    } catch (err) {
      this.cache.delete(feature.id);
      return { kind: 'error', message: err instanceof Error ? err.message : String(err) };
    }
  }
}
